import { useMemo, useState } from "react";
import { programApi } from "@bw/shared/api";
import {
  useProgramPreviewIdentity,
  useProgramPreviewPage,
} from "@bw/shared/hooks";
import type { ProgramSessionPreviewPage } from "@bw/shared/types/generated";

const EIGHT_WEEKS_MS = 8 * 7 * 24 * 60 * 60 * 1000;
export function deriveSessionWindow(): {
  window_start: string;
  window_end: string;
} {
  const now = Date.now();
  return {
    window_start: new Date(now).toISOString(),
    window_end: new Date(now + EIGHT_WEEKS_MS).toISOString(),
  };
}

/** Canonical live owner pages, including an exact selected Track for drop-in. */
export function useProgramSessions(
  programId: string | undefined,
  enabled = true,
  trackId?: string,
) {
  const identity = useProgramPreviewIdentity();
  const [windowGeneration, setWindowGeneration] = useState(0);
  const window = useMemo(deriveSessionWindow, [
    programId,
    identity,
    windowGeneration,
  ]);
  const query = useProgramPreviewPage<ProgramSessionPreviewPage>({
    scope: [
      identity,
      "classes",
      programId ?? "",
      "authenticated",
      "sessions",
      window.window_start,
      window.window_end,
      trackId ?? null,
    ],
    enabled: Boolean(programId) && enabled,
    load: (cursor, signal) =>
      programApi.getSessionPreview(
        programId as string,
        {
          ...window,
          limit: 50,
          ...(trackId ? { track_id: trackId } : {}),
          ...(cursor ? { cursor } : {}),
        },
        signal,
      ),
  });
  return {
    ...query,
    restart: () => {
      query.restart();
      setWindowGeneration((current) => current + 1);
    },
  };
}
